import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Maximize2, Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { NEGATIVE, POSITIVE, SELECTED, ownerColorsFrom } from '../graphColors'
import type { WorldGraphEdge, WorldGraphNode } from '../types'

interface GraphCanvasProps {
  nodes: WorldGraphNode[]
  edges: WorldGraphEdge[]
  teamNames: Record<string, string>
  selectedNodeId: string | null
  selectedEdgeId: string | null
  onSelectNode: (id: string) => void
  onSelectEdge: (id: string) => void
  containerClassName?: string
}

// --- layout geometry --------------------------------------------------------
const NODE_W = 168
const NODE_H = 44
const V_GAP = 22
const COL_GUTTER = 104
const COL_W = NODE_W + COL_GUTTER
const TOP_PAD = 52
const SIDE_PAD = 28
const BOTTOM_PAD = 28
const FIT_PAD = 36
const MIN_W = 280 // most zoomed-in world width
const MAX_W = 7000 // most zoomed-out world width

interface Placed {
  node: WorldGraphNode
  x: number
  y: number
  cx: number
  cy: number
  col: number
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v))

const lineWidth = (magnitude: number) => 1.2 + clamp(magnitude, 0, 1) * 2.4

const delayText = (lag: number) =>
  lag === 0 ? 'at once' : lag === 1 ? 'after 1 round' : `after ${lag} rounds`

/**
 * Dependency-free SVG causal-graph view, ported from capstone's Game Builder.
 * Stocks sit in columns by category; links are border-anchored curves,
 * coloured by sign (arrowhead for +, bar for -) and thickened by strength.
 * Zoom, pan, fit, and hover or click to focus.
 */
export default function GraphCanvas({
  nodes,
  edges,
  teamNames,
  selectedNodeId,
  selectedEdgeId,
  onSelectNode,
  onSelectEdge,
  containerClassName,
}: GraphCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ cw: 900, ch: 500 })
  const [view, setView] = useState({ x: 0, y: 0, w: 1200 })
  const [hoverId, setHoverId] = useState<string | null>(null)
  const panRef = useRef<{
    sx: number
    sy: number
    vx: number
    vy: number
  } | null>(null)
  const [grabbing, setGrabbing] = useState(false)

  const ownerColors = useMemo(() => ownerColorsFrom(nodes), [nodes])

  // --- layout ---------------------------------------------------------------
  const layout = useMemo(() => {
    // Columns in the order the model first used each category.
    const ordered = Array.from(new Set(nodes.map((n) => n.category)))
    const placed = new Map<string, Placed>()
    const columns: { category: string; x: number }[] = []
    let tallest = 0
    ordered.forEach((category, colIdx) => {
      const colX = SIDE_PAD + colIdx * COL_W
      columns.push({ category, x: colX })
      const colNodes = nodes.filter((n) => n.category === category)
      colNodes.forEach((node, rowIdx) => {
        const y = TOP_PAD + rowIdx * (NODE_H + V_GAP)
        placed.set(node.id, {
          node,
          x: colX,
          y,
          cx: colX + NODE_W / 2,
          cy: y + NODE_H / 2,
          col: colIdx,
        })
      })
      tallest = Math.max(tallest, colNodes.length)
    })
    const cols = Math.max(1, ordered.length)
    const contentW = SIDE_PAD * 2 + (cols - 1) * COL_W + NODE_W
    const contentH = TOP_PAD + tallest * (NODE_H + V_GAP) - V_GAP + BOTTOM_PAD
    return { placed, columns, contentW, contentH }
  }, [nodes])

  // --- track container size -------------------------------------------------
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      setSize({ cw: el.clientWidth || 900, ch: el.clientHeight || 500 })
    })
    ro.observe(el)
    setSize({ cw: el.clientWidth || 900, ch: el.clientHeight || 500 })
    return () => ro.disconnect()
  }, [])

  // --- fit view to content --------------------------------------------------
  const fit = useCallback(() => {
    const cw = Math.max(1, size.cw)
    const ch = Math.max(1, size.ch)
    const aspect = ch / cw
    const needW = layout.contentW + FIT_PAD * 2
    const needH = layout.contentH + FIT_PAD * 2
    const w = clamp(Math.max(needW, needH / aspect), MIN_W, MAX_W)
    const vh = w * aspect
    setView({
      x: (layout.contentW - w) / 2,
      y: (layout.contentH - vh) / 2,
      w,
    })
  }, [size.cw, size.ch, layout.contentW, layout.contentH])

  useEffect(() => {
    fit()
  }, [fit])

  const vh = view.w * (size.ch / Math.max(1, size.cw))

  // --- zoom -----------------------------------------------------------------
  const zoomBy = useCallback(
    (factor: number, clientX?: number, clientY?: number) => {
      const rect = containerRef.current?.getBoundingClientRect()
      setView((v) => {
        const cw = Math.max(1, containerRef.current?.clientWidth ?? 1)
        const wpp = v.w / cw
        const px = rect && clientX != null ? clientX - rect.left : cw / 2
        const py =
          rect && clientY != null
            ? clientY - rect.top
            : (containerRef.current?.clientHeight ?? 0) / 2
        const wx = v.x + px * wpp
        const wy = v.y + py * wpp
        const nw = clamp(v.w * factor, MIN_W, MAX_W)
        const k = nw / v.w
        return { x: wx - (wx - v.x) * k, y: wy - (wy - v.y) * k, w: nw }
      })
    },
    []
  )

  // wheel zoom (native, non-passive so we can preventDefault)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      zoomBy(e.deltaY > 0 ? 1.12 : 0.89, e.clientX, e.clientY)
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [zoomBy])

  // --- pan ------------------------------------------------------------------
  const onPointerDown = (e: React.PointerEvent) => {
    panRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y }
    setGrabbing(true)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const p = panRef.current
    if (!p) return
    const wpp = view.w / Math.max(1, size.cw)
    setView((v) => ({
      ...v,
      x: p.vx - (e.clientX - p.sx) * wpp,
      y: p.vy - (e.clientY - p.sy) * wpp,
    }))
  }
  const endPan = () => {
    panRef.current = null
    setGrabbing(false)
  }

  // --- focus / emphasis -----------------------------------------------------
  const focusId = hoverId ?? selectedNodeId
  const connected = useMemo(() => {
    if (!focusId) return null
    const set = new Set<string>([focusId])
    edges.forEach((e) => {
      if (e.source === focusId) set.add(e.target)
      if (e.target === focusId) set.add(e.source)
    })
    return set
  }, [focusId, edges])

  const hasFocus = !!selectedEdgeId || !!focusId
  const edgeActive = (e: WorldGraphEdge) => {
    if (selectedEdgeId) return e.id === selectedEdgeId
    if (focusId) return e.source === focusId || e.target === focusId
    return true
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-hidden rounded-lg border bg-background',
        containerClassName ?? 'h-[520px]'
      )}
      data-testid='world-graph-canvas'
    >
      <div className='absolute right-2 top-2 z-10 flex flex-col gap-1 rounded-md border bg-white/90 p-1 shadow-sm backdrop-blur'>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          aria-label='Zoom in'
          onClick={() => zoomBy(0.8)}
        >
          <Plus className='h-4 w-4' />
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          aria-label='Zoom out'
          onClick={() => zoomBy(1.25)}
        >
          <Minus className='h-4 w-4' />
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          className='h-8 w-8'
          aria-label='Fit to view'
          onClick={fit}
        >
          <Maximize2 className='h-4 w-4' />
        </Button>
      </div>
      <span className='pointer-events-none absolute bottom-2 left-3 z-10 text-[11px] text-muted-foreground'>
        Scroll to zoom · drag to pan · hover or click to focus
      </span>

      <svg
        width='100%'
        height='100%'
        viewBox={`${view.x} ${view.y} ${view.w} ${vh}`}
        preserveAspectRatio='xMidYMid meet'
        role='img'
        aria-label={`Causal world graph: ${nodes.length} stocks in ${layout.columns.length} categories, ${edges.length} links. Positive links end in an arrowhead, negative links end in a bar.`}
        className={cn(
          'block touch-none select-none',
          grabbing ? 'cursor-grabbing' : 'cursor-grab'
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerLeave={endPan}
      >
        <defs>
          <marker
            id='wg-arrow-pos'
            viewBox='0 0 10 10'
            refX='8'
            refY='5'
            markerWidth='7'
            markerHeight='7'
            orient='auto-start-reverse'
          >
            <path d='M 0 0 L 10 5 L 0 10 z' fill={POSITIVE} />
          </marker>
          {/* negative = inhibition bar (shape cue, not colour alone) */}
          <marker
            id='wg-arrow-neg'
            viewBox='0 0 12 12'
            refX='4'
            refY='6'
            markerWidth='8'
            markerHeight='8'
            orient='auto-start-reverse'
          >
            <path d='M 4 1 L 4 11' stroke={NEGATIVE} strokeWidth='2.5' />
          </marker>
        </defs>

        {/* category bands */}
        {layout.columns.map((col, i) => (
          <rect
            key={`band-${col.category}`}
            x={col.x - COL_GUTTER / 2}
            y={0}
            width={COL_W}
            height={layout.contentH}
            fill='hsl(var(--muted))'
            opacity={i % 2 === 0 ? 0.6 : 0.25}
          />
        ))}

        {/* column headers */}
        {layout.columns.map((col) => (
          <text
            key={`hdr-${col.category}`}
            x={col.x + NODE_W / 2}
            y={26}
            textAnchor='middle'
            fill='hsl(var(--muted-foreground))'
            fontSize={12}
            fontWeight={700}
            style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            {col.category}
          </text>
        ))}

        {/* links (under stocks) */}
        {edges.map((edge) => {
          const from = layout.placed.get(edge.source)
          const to = layout.placed.get(edge.target)
          if (!from || !to) return null
          const active = edgeActive(edge)
          const isSelected = edge.id === selectedEdgeId
          const color = edge.sign === '+' ? POSITIVE : NEGATIVE
          const width = lineWidth(edge.magnitude)

          let sx: number
          let tx: number
          let c1x: number
          let c2x: number
          const c1y = from.cy
          const c2y = to.cy
          const sy = from.cy
          const ty = to.cy
          if (from.col === to.col) {
            // same column: bow out to the right
            sx = from.x + NODE_W
            tx = to.x + NODE_W
            const bend = 48 + Math.abs(ty - sy) * 0.18
            c1x = sx + bend
            c2x = tx + bend
          } else {
            const ltr = to.col > from.col
            sx = ltr ? from.x + NODE_W : from.x
            tx = ltr ? to.x : to.x + NODE_W
            const handle = Math.max(48, Math.abs(tx - sx) * 0.42)
            c1x = ltr ? sx + handle : sx - handle
            c2x = ltr ? tx - handle : tx + handle
          }
          const d = `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tx} ${ty}`
          const verb = edge.sign === '+' ? 'raises' : 'lowers'
          return (
            <g
              key={edge.id}
              className='cursor-pointer'
              onClick={(e) => {
                e.stopPropagation()
                onSelectEdge(edge.id)
              }}
            >
              <title>{`${from.node.label} ${verb} ${to.node.label} (strength ${edge.magnitude}, ${delayText(edge.lag)}): ${edge.mechanism}`}</title>
              {/* wide transparent hit area so thin links are easy to click */}
              <path d={d} fill='none' stroke='transparent' strokeWidth={16} />
              <path
                d={d}
                fill='none'
                stroke={color}
                strokeWidth={isSelected ? width + 1.25 : width}
                strokeLinecap='round'
                markerEnd={`url(#wg-arrow-${edge.sign === '+' ? 'pos' : 'neg'})`}
                opacity={active ? 1 : hasFocus ? 0.08 : 0.65}
              />
            </g>
          )
        })}

        {/* stocks (over links) */}
        {nodes.map((stock) => {
          const placed = layout.placed.get(stock.id)
          if (!placed) return null
          const isSelected = stock.id === selectedNodeId
          const dimmed = connected ? !connected.has(stock.id) : false
          const ownerColor = stock.owner ? ownerColors.get(stock.owner) : null
          const accentColor = isSelected
            ? SELECTED
            : ownerColor || 'hsl(var(--muted-foreground))'
          const label =
            stock.label.length > 22
              ? `${stock.label.slice(0, 21)}…`
              : stock.label
          const owner = stock.owner
            ? teamNames[stock.owner] || stock.owner
            : 'Shared'
          return (
            <g
              key={stock.id}
              opacity={dimmed ? 0.28 : 1}
              className='cursor-pointer'
              onClick={(e) => {
                e.stopPropagation()
                onSelectNode(stock.id)
              }}
              onPointerEnter={() => setHoverId(stock.id)}
              onPointerLeave={() => setHoverId(null)}
            >
              <title>{`${stock.label} (${owner}, ${stock.type}, starts at ${stock.value})`}</title>
              <rect
                x={placed.x}
                y={placed.y}
                width={NODE_W}
                height={NODE_H}
                rx={9}
                fill='hsl(var(--card))'
                stroke={
                  isSelected ? SELECTED : ownerColor || 'hsl(var(--border))'
                }
                strokeWidth={isSelected ? 2.5 : ownerColor ? 1.5 : 1}
              />
              <rect
                x={placed.x}
                y={placed.y + 8}
                width={ownerColor ? 4 : 3}
                height={NODE_H - 16}
                rx={1.5}
                fill={accentColor}
                opacity={isSelected ? 1 : ownerColor ? 0.75 : 0.4}
              />
              <text
                x={placed.x + NODE_W / 2 + 2}
                y={placed.y + NODE_H / 2 + 4}
                textAnchor='middle'
                fill='hsl(var(--foreground))'
                fontSize={12.5}
                fontWeight={isSelected ? 600 : 500}
              >
                {label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
